import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { runInAction } from 'mobx'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'

import type { ReportEditorStore } from 'modules/reports/lib/Store'
import { usePromptContext } from 'modules/reports/lib/promptContext'
import type { ConditionParams } from 'modules/reports/store/models/Condition'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'
import type Report from 'modules/reports/store/models/Report'
import type { ReportParams, SaveParams } from 'modules/reports/store/models/Report'

import { useReportTracking } from './useReportTracking'

type SaveItem = ReportParams | ProjectionParams | ConditionParams

interface Props {
  report: Report
  store: ReportEditorStore
  clone?: boolean
  onSuccess?: () => void
}

const nullifyId = ({ id: _id, ...rest }: SaveItem): SaveItem => ({
  ...rest,
  id: null,
})

const useSaveReport = ({ store, report, onSuccess, clone = false }: Props) => {
  const { navigation } = useNavigation()
  const { track } = useReportTracking(store)

  const navigate = useNavigate()
  const [_, setPromptStatus] = usePromptContext()

  const { performAction: onSaveReport, running } = useAsyncAction(
    async () => {
      const serializedData: SaveParams = store.serializeForSave()

      const model = report.collection || report
      const { success, value } = await asyncAction(async () => {
        if (clone) {
          return model.rpc('save', {
            report: {
              ...nullifyId(serializedData.report),
              origin: report.origin,
            },
            projections: serializedData.projections.map(nullifyId),
            filters: serializedData.filters.map((set) => set.map(nullifyId)),
            sql_query: serializedData.sql_query,
            sql_configuration: serializedData.sql_configuration,
          })
        }

        return model.rpc(report.isNew ? 'save' : 'change', store.serializeForSave())
      })

      if (success) {
        if (clone) {
          track(`new report cloned from id[${serializedData.report.id}] saved`, {})
        }

        runInAction(() => {
          store.saved = true
        })

        // An issue was fixed regarding page transitions and the display of a browser prompt when switching pages without saving widget changes.
        // However, a recent update to the React Router library disrupted the API used by usePrompt and useBlock due to changes in implementation
        // and interface.
        //
        // To address this, a provider was introduced to share the usePrompt state between the toolbar and shell components, allowing context
        // updates and state synchronization using flushSync.
        //
        // This solution ensures the prompt's accurate display before page redirection and suggests using flushSync to handle such API changes.
        //
        // More info here: https://github.com/factorialco/factorial/pull/24103
        flushSync(() => {
          setPromptStatus(store.isDirty && !store.saved)
        })

        onSuccess?.()
        navigate(
          navigation.reports.dashboards.dashboardId.list.fullPath({
            dashboard_id: value.dashboard_id,
          })
        )
      } else {
        store.reportForm.setErrors(value.payload)
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return {
    onSaveReport,
    saving: running,
  }
}

export default useSaveReport
